import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useTeacherSearch } from "hooks/useTeacherSearch";
import { request } from "hooks/useOkoApi/useOkoApi";

const StyledCard = styled(Card)`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  margin-bottom: ${({ $roomy }) => ($roomy ? "24px" : "16px")};
`;

const CardHeader = styled.div`
  background: ${colors.milkGlass200};
  padding: ${({ $roomy }) => ($roomy ? "16px 20px" : "12px 16px")};
  border-bottom: 1px solid ${colors.milkGlass400};
  font-weight: 600;
  color: ${colors.nightlyWoods900};
`;

const Section = styled.div`
  display: flex;
  padding: ${({ $roomy }) => ($roomy ? "12px 20px" : "12px 16px")};
  border-bottom: 1px solid ${colors.milkGlass200};
  &:last-child {
    border-bottom: none;
  }
`;

const SectionLabel = styled.div`
  flex: 0 0 180px;
  color: ${colors.nightlyWoods600};
  font-weight: 500;
`;

const SectionValue = styled.div`
  flex: 1;
  color: ${colors.nightlyWoods900};
`;

const ResultList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
`;

const ResultRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid ${colors.milkGlass200};
`;

const CoTeacherList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CoTeacherRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid ${colors.milkGlass200};
  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const TeacherEmail = styled.div`
  font-size: 13px;
  color: ${colors.nightlyWoods600};
`;

const SearchStatus = styled.div`
  padding: 12px 16px 0;
  color: ${colors.nightlyWoods600};
`;

const LinkedOn = styled.div`
  font-size: 13px;
  color: ${colors.nightlyWoods600};
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const EDIT_PAGE = "edit";
export const DETAIL_PAGE = "detail";

const TITLE_ID = "co-teacher-card-title";
const CO_TEACHER_OF_ID = "co-teacher-of-label";
const CO_TEACHERS_ID = "co-teachers-label";
const SEARCH_ID = "co-teacher-search";
const SEARCH_HELP_ID = "co-teacher-search-help";
const LINK_DIALOG_TITLE_ID = "co-teacher-link-dialog-title";
const REMOVE_DIALOG_TITLE_ID = "co-teacher-remove-dialog-title";

const LINK_DIALOG = "link";
const REMOVE_DIALOG = "remove";
const NOTHING = "-";
const SUPERUSERS_ONLY = "Only superusers can link or remove co-teachers.";
const LINK_FAILED = "Failed to link the co-teacher. Please try again.";
const REMOVAL_FAILED = "Failed to remove the link. Please try again.";
const ALREADY_REMOVED = "This link had already been removed.";
const STATUSES_WITH_THE_SERVERS_WORDS = [404, 409, 422];

const fullName = (teacher) =>
  [teacher.first_name, teacher.last_name]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");

const teacherName = (teacher) =>
  fullName(teacher) || teacher.email || teacher.educator_id || teacher.id;

const linkDate = (link) => new Date(link.linked_at).toLocaleDateString();

const userDetailPath = (id) =>
  generateRoutePath(constants.USER_ADMIN_DETAIL_ROUTE_NAME, { id });

const uniqueById = (teachers) =>
  teachers.filter((teacher, index) => teachers.findIndex((t) => t.id === teacher.id) === index);

const countOfTeachersFound = (count) =>
  count === 1 ? "1 teacher found" : `${count} teachers found`;

const ownClassesArchivedBy = (link) =>
  link.own_classes_archived === undefined ? "" : ` Classes archived: ${link.own_classes_archived}.`;

const isEducator = (user) => (user?.roles || []).includes("educator");

const removalOf = (coTeacherId, coTeacher, primary) => ({
  coTeacherId,
  coTeacherName: teacherName(coTeacher),
  primaryName: teacherName(primary),
});

const linkRefusalText = (err) => {
  if (err.status === 403) return SUPERUSERS_ONLY;
  const hasTheServersWords =
    STATUSES_WITH_THE_SERVERS_WORDS.includes(err.status) && typeof err.detail === "string";
  if (hasTheServersWords) return err.detail;
  return LINK_FAILED;
};

const removalRefusalText = (err) => {
  if (err.status === 403) return SUPERUSERS_ONLY;
  if (err.status === 404) return ALREADY_REMOVED;
  return REMOVAL_FAILED;
};

const useAnsweredTerm = (term, searchLoading) => {
  const [answeredTerm, setAnsweredTerm] = useState(null);
  const latestTerm = useRef(term);
  const askedTerm = useRef(null);
  latestTerm.current = term;

  useEffect(() => {
    if (searchLoading) {
      askedTerm.current = latestTerm.current;
    } else if (askedTerm.current !== null) {
      setAnsweredTerm(askedTerm.current);
      askedTerm.current = null;
    }
  }, [searchLoading]);

  return answeredTerm;
};

const ConfirmDialog = ({
  show,
  titleId,
  title,
  confirmLabel,
  busyLabel,
  confirmVariant,
  busy,
  onCancel,
  onConfirm,
  onExited,
  children,
}) => (
  <Modal
    show={show}
    onHide={onCancel}
    onExited={onExited}
    restoreFocus={false}
    aria-labelledby={titleId}
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title id={titleId}>{title}</Modal.Title>
    </Modal.Header>
    {children}
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant={confirmVariant} onClick={onConfirm} disabled={busy}>
        {busy ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
            {busyLabel}
          </>
        ) : (
          confirmLabel
        )}
      </Button>
    </Modal.Footer>
  </Modal>
);

const OwnClassesWarning = ({ coTeacherName, ownClasses }) => (
  <Alert variant="warning" transition={false} role={null}>
    {ownClasses.to_archive === 0 ? (
      `${coTeacherName} has no active classes of their own.`
    ) : (
      <>
        <p>
          {coTeacherName}'s own classes will stop appearing for {coTeacherName}. This cannot be
          undone, not even by removing the link.
        </p>
        <div>Classes archived (their group codes stop working): {ownClasses.to_archive}</div>
      </>
    )}
  </Alert>
);

const LinkDialogBody = ({ coTeacherName, primaryName, ownClasses }) => (
  <Modal.Body>
    <p>
      {coTeacherName} will become a co-teacher of {primaryName}.
    </p>
    <ul>
      <li>
        {coTeacherName} will see and edit {primaryName}'s classes, group codes, sessions and
        reports.
      </li>
      <li>{coTeacherName} keeps signing in with their own account.</li>
      <li>Safety alerts and school reports stay with {primaryName}.</li>
    </ul>
    {ownClasses && <OwnClassesWarning coTeacherName={coTeacherName} ownClasses={ownClasses} />}
  </Modal.Body>
);

const RemoveDialogBody = ({ coTeacherName, primaryName }) => (
  <Modal.Body>
    {coTeacherName} will no longer see {primaryName}'s classes, group codes, sessions and
    reports. The classes that were archived when {coTeacherName} was linked are not restored.
  </Modal.Body>
);

const MessageArea = forwardRef(
  ({ message, searching, termWithNoTeacher, teachersFound, searchFailed }, ref) => (
    <div ref={ref} tabIndex={-1}>
      <div role="status">
        {message?.variant === "success" && (
          <Alert variant="success" transition={false} role={null} className="m-3">
            {message.text}
          </Alert>
        )}
        {searching && (
          <SearchStatus>
            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
            <span>Searching…</span>
          </SearchStatus>
        )}
        {termWithNoTeacher && (
          <SearchStatus>No teachers found for "{termWithNoTeacher}".</SearchStatus>
        )}
        {teachersFound > 0 && <SrOnly>{countOfTeachersFound(teachersFound)}</SrOnly>}
      </div>
      <div role="alert">
        {message?.variant === "danger" && (
          <Alert variant="danger" transition={false} role={null} className="m-3">
            {message.text}
          </Alert>
        )}
        {!message && searchFailed && (
          <SearchStatus>Failed to search teachers. Please try again.</SearchStatus>
        )}
      </div>
    </div>
  )
);

const TeachersFound = ({ teachers, onSelect }) => (
  <ResultList>
    {teachers.map((teacher) => (
      <ResultRow key={teacher.id}>
        <div>
          <div>{teacherName(teacher)}</div>
          <TeacherEmail>{teacher.email}</TeacherEmail>
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          aria-label={`Select ${teacherName(teacher)}`}
          onClick={(e) => onSelect(teacher, e.currentTarget)}
        >
          Select
        </Button>
      </ResultRow>
    ))}
  </ResultList>
);

const CoTeachersLinked = ({ coTeachers, onRemove }) => (
  <CoTeacherList>
    {coTeachers.map((coTeacher) => (
      <CoTeacherRow key={coTeacher.educator_id}>
        <div>
          <Link to={userDetailPath(coTeacher.educator_id)}>{teacherName(coTeacher)}</Link>
          <TeacherEmail>{coTeacher.email}</TeacherEmail>
          <LinkedOn>Linked on {linkDate(coTeacher)}</LinkedOn>
        </div>
        {onRemove && (
          <Button
            variant="outline-danger"
            size="sm"
            aria-label={`Remove ${teacherName(coTeacher)} as co-teacher`}
            onClick={(e) => onRemove(coTeacher, e.currentTarget)}
          >
            Remove
          </Button>
        )}
      </CoTeacherRow>
    ))}
  </CoTeacherList>
);

export const CoTeacherCard = ({ user, variant, editable, onUserChange }) => {
  const { searchTerm, setSearchTerm, searchResults, searchLoading, searchError, clearSearch } =
    useTeacherSearch(user?.school_id);
  const [linkCandidate, setLinkCandidate] = useState(null);
  const [removal, setRemoval] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const focusAfterDialog = useRef(null);
  const messageArea = useRef(null);
  const linkChanges = useRef(0);
  const term = searchTerm.trim();
  const answeredTerm = useAnsweredTerm(term, searchLoading);
  const candidates = uniqueById(searchResults).filter((teacher) => teacher.id !== user?.id);
  const noTeacherFound =
    !searchLoading &&
    !searchError &&
    answeredTerm === term &&
    candidates.length === 0;
  const coTeacherOf = user?.co_teacher_of || null;
  const coTeachers = user?.co_teachers || [];

  if (!isEducator(user) && !coTeacherOf && coTeachers.length === 0) return null;

  const showMessage = (messageVariant, text) => {
    setMessage({ variant: messageVariant, text });
    focusAfterDialog.current = messageArea.current;
  };

  const handleSearchTermChange = (e) => {
    setMessage(null);
    setSearchTerm(e.target.value);
  };

  const openLinkDialog = (teacher, opener) => {
    setMessage(null);
    focusAfterDialog.current = opener;
    setLinkCandidate(teacher);
    setOpenDialog(LINK_DIALOG);
  };

  const openRemoveDialog = (linkToRemove, opener) => {
    setMessage(null);
    focusAfterDialog.current = opener;
    setRemoval(linkToRemove);
    setOpenDialog(REMOVE_DIALOG);
  };

  const closeDialog = () => {
    if (!busy) setOpenDialog(null);
  };

  const focusAfterDialogHasLeft = () => focusAfterDialog.current?.focus();

  const reloadUser = async () => {
    const linkChangesWhenAsked = linkChanges.current;
    try {
      const loaded = await request(`admin/users/${user.id}`, { method: "GET" });
      if (linkChanges.current === linkChangesWhenAsked) onUserChange(loaded);
    } catch {
      // The refusal already on screen stands; the admin can reload the page.
    }
  };

  const handleLink = async () => {
    setBusy(true);
    try {
      const link = await request(`admin/users/${user.id}/co-teacher-of`, {
        method: "POST",
        payload: { primary_educator_id: linkCandidate.id },
      });
      linkChanges.current += 1;
      const coTeacherName = teacherName(user);
      const primaryName = teacherName(link);
      onUserChange({ co_teacher_of: link, own_classes_if_linked: null });
      clearSearch();
      showMessage(
        "success",
        `${coTeacherName} is now a co-teacher of ${primaryName}.${ownClassesArchivedBy(link)} ${coTeacherName} will see ${primaryName}'s classes the next time they open OKO.`
      );
    } catch (err) {
      showMessage("danger", linkRefusalText(err));
      if (err.status === 409) reloadUser();
    } finally {
      setBusy(false);
      setOpenDialog(null);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await request(`admin/users/${removal.coTeacherId}/co-teacher-of`, { method: "DELETE" });
      linkChanges.current += 1;
      onUserChange(
        removal.coTeacherId === user.id
          ? { co_teacher_of: null }
          : { co_teachers: coTeachers.filter((c) => c.educator_id !== removal.coTeacherId) }
      );
      showMessage(
        "success",
        `${removal.coTeacherName} is no longer a co-teacher of ${removal.primaryName}.`
      );
      if (removal.coTeacherId === user.id) reloadUser();
    } catch (err) {
      showMessage("danger", removalRefusalText(err));
      if (err.status === 404) reloadUser();
    } finally {
      setBusy(false);
      setOpenDialog(null);
    }
  };

  const renderLinkOffer = () => {
    if (!isEducator(user)) {
      return <div>Only a user with the Educator role can be linked as a co-teacher.</div>;
    }
    if (!user.school_id) {
      return <div>Assign this teacher to a school before linking them as a co-teacher.</div>;
    }
    return (
      <Form.Group controlId={SEARCH_ID} className="mt-3">
        <Form.Label>Find the primary teacher</Form.Label>
        <Form.Control
          type="text"
          placeholder="Search by name or email"
          aria-describedby={SEARCH_HELP_ID}
          value={searchTerm}
          onChange={handleSearchTermChange}
        />
        <Form.Text id={SEARCH_HELP_ID} className="text-muted">
          Only teachers at {user.school_name} are listed.
        </Form.Text>
        {candidates.length > 0 && <TeachersFound teachers={candidates} onSelect={openLinkDialog} />}
      </Form.Group>
    );
  };

  const renderCoTeacherOf = () => {
    if (!coTeacherOf) {
      if (!editable) return NOTHING;
      return (
        <>
          <div>Not a co-teacher of anyone.</div>
          {renderLinkOffer()}
        </>
      );
    }
    return (
      <>
        <Link to={userDetailPath(coTeacherOf.educator_id)}>{teacherName(coTeacherOf)}</Link>
        <TeacherEmail>{coTeacherOf.email}</TeacherEmail>
        <LinkedOn>
          Linked on {linkDate(coTeacherOf)}
          {editable && coTeacherOf.granted_by_email && ` by ${coTeacherOf.granted_by_email}`}
        </LinkedOn>
        {variant === DETAIL_PAGE && (
          <div>
            Has access to {teacherName(coTeacherOf)}'s classes. Their own classes were archived
            when they were linked and are still listed below.
          </div>
        )}
        {editable && (
          <Button
            variant="outline-danger"
            size="sm"
            className="mt-2"
            onClick={(e) =>
              openRemoveDialog(removalOf(user.id, user, coTeacherOf), e.currentTarget)
            }
          >
            Remove link
          </Button>
        )}
      </>
    );
  };

  const roomy = variant === DETAIL_PAGE;

  const removeCoTeacher = (coTeacher, opener) =>
    openRemoveDialog(removalOf(coTeacher.educator_id, coTeacher, user), opener);

  return (
    <StyledCard role="region" aria-labelledby={TITLE_ID} $roomy={roomy}>
      <CardHeader id={TITLE_ID} $roomy={roomy}>
        Co-teacher
      </CardHeader>
      <Card.Body style={{ padding: 0 }}>
        {editable && (
          <MessageArea
            ref={messageArea}
            message={message}
            searching={searchLoading}
            termWithNoTeacher={noTeacherFound ? term : null}
            teachersFound={searchLoading ? 0 : candidates.length}
            searchFailed={Boolean(searchError)}
          />
        )}
        <Section role="group" aria-labelledby={CO_TEACHER_OF_ID} $roomy={roomy}>
          <SectionLabel id={CO_TEACHER_OF_ID}>Co-teacher of</SectionLabel>
          <SectionValue>{renderCoTeacherOf()}</SectionValue>
        </Section>
        <Section role="group" aria-labelledby={CO_TEACHERS_ID} $roomy={roomy}>
          <SectionLabel id={CO_TEACHERS_ID}>Co-teachers</SectionLabel>
          <SectionValue>
            {coTeachers.length > 0 ? (
              <CoTeachersLinked
                coTeachers={coTeachers}
                onRemove={editable ? removeCoTeacher : null}
              />
            ) : (
              <div>{editable ? "No co-teachers." : NOTHING}</div>
            )}
            {editable && (
              <Form.Text className="text-muted">
                To add a co-teacher, open that teacher's page and set "Co-teacher of".
              </Form.Text>
            )}
          </SectionValue>
        </Section>
        {!editable && variant === EDIT_PAGE && (
          <Section>
            <Form.Text className="text-muted">{SUPERUSERS_ONLY}</Form.Text>
          </Section>
        )}
      </Card.Body>

      <ConfirmDialog
        show={openDialog === LINK_DIALOG}
        titleId={LINK_DIALOG_TITLE_ID}
        title="Link co-teacher"
        confirmLabel="Link co-teacher"
        busyLabel="Linking…"
        confirmVariant="primary"
        busy={busy}
        onCancel={closeDialog}
        onConfirm={handleLink}
        onExited={focusAfterDialogHasLeft}
      >
        {linkCandidate && (
          <LinkDialogBody
            coTeacherName={teacherName(user)}
            primaryName={teacherName(linkCandidate)}
            ownClasses={user.own_classes_if_linked}
          />
        )}
      </ConfirmDialog>

      <ConfirmDialog
        show={openDialog === REMOVE_DIALOG}
        titleId={REMOVE_DIALOG_TITLE_ID}
        title="Remove co-teacher link"
        confirmLabel="Remove link"
        busyLabel="Removing…"
        confirmVariant="danger"
        busy={busy}
        onCancel={closeDialog}
        onConfirm={handleRemove}
        onExited={focusAfterDialogHasLeft}
      >
        {removal && (
          <RemoveDialogBody
            coTeacherName={removal.coTeacherName}
            primaryName={removal.primaryName}
          />
        )}
      </ConfirmDialog>
    </StyledCard>
  );
};
